import type { CancellationOutcome } from './proration.js';

/** Rótulo legível do meio de pagamento (Mercado Pago e outros). */
export function nomeMeioPagamento(method: string | null | undefined): string {
  const nomes: Record<string, string> = {
    credit_card: 'cartão de crédito',
    debit_card: 'cartão de débito',
    pix: 'Pix',
    boleto: 'boleto bancário',
    paypal: 'PayPal',
    wallet: 'carteira digital',
    manual: 'liberação manual',
  };
  if (!method) return 'meio de pagamento original';
  return nomes[method] ?? method;
}

/** Prazo típico de devolução conforme o meio usado na compra. */
export function prazoEstornoMeioPagamento(method: string | null | undefined): string {
  switch (method) {
    case 'credit_card':
      return (
        'No cartão de crédito, o estorno costuma aparecer na fatura atual ou na ' +
        'seguinte, em até 5 a 10 dias úteis, conforme o banco emissor.'
      );
    case 'debit_card':
      return (
        'No cartão de débito, o valor volta para a conta corrente em até 5 dias ' +
        'úteis, conforme o banco.'
      );
    case 'pix':
      return 'No Pix, a devolução cai na mesma conta em até 1 dia útil.';
    case 'boleto':
      return (
        'O valor é devolvido pelo Mercado Pago para os dados (e-mail e CPF) usados na compra. ' +
        'Se não aparecer em até 10 dias úteis, entre em contato com o suporte do Mercado Pago ' +
        'informando o número do pedido (https://www.mercadopago.com.br/ajuda).'
      );
    default:
      return (
        'O prazo de crédito depende do meio de pagamento e do banco — em geral ' +
        'entre 1 e 10 dias úteis.'
      );
  }
}

export interface TextoCancelamentoInput {
  outcome: CancellationOutcome;
  refundCents: number;
  /** Data ISO ou já formatada para exibição. */
  accessUntil: string;
  metodoPagamento: string | null | undefined;
  reembolsoStatus?: 'done' | 'pending' | null;
  revokeImmediately: boolean;
  /** Formata centavos para moeda (ex.: R$ 49,90). */
  formatarMoeda: (cents: number) => string;
  /** Formata data para exibição. */
  formatarData: (iso: string) => string;
}

export function tituloSituacaoAssinatura(
  canceladoEm: string | null | undefined,
  status: string,
): string {
  if (canceladoEm) {
    if (status === 'expired') return 'Assinatura cancelada';
    if (status === 'cancelled') return 'Assinatura cancelada';
    return 'Assinatura cancelada';
  }
  if (status === 'expired') return 'Assinatura vencida';
  if (status === 'cancelled') return 'Assinatura cancelada';
  return 'Assinatura ativa';
}

/** Parágrafos para o aviso fixo na área Conta. */
export function paragrafosCancelamento(input: TextoCancelamentoInput): string[] {
  const ate = input.formatarData(input.accessUntil);
  const meio = nomeMeioPagamento(input.metodoPagamento);
  const prazo = prazoEstornoMeioPagamento(input.metodoPagamento);

  if (input.outcome === 'full_refund') {
    const valor = input.formatarMoeda(input.refundCents);
    const estorno =
      input.reembolsoStatus === 'done'
        ? `O reembolso integral de ${valor} já foi confirmado pelo Mercado Pago.`
        : input.reembolsoStatus === 'pending'
          ? `O reembolso integral de ${valor} foi iniciado no Mercado Pago e está sendo processado.`
          : `Vamos devolver ${valor} integralmente por estar dentro dos 7 dias de arrependimento.`;

    return [
      'Sua assinatura foi cancelada e não será renovada.',
      estorno,
      `A devolução será feita pelo mesmo ${meio} usado na compra. ${prazo}`,
      'Seu acesso à plataforma foi encerrado. Todo o seu progresso continua guardado caso queira voltar.',
      'Enviamos os detalhes por e-mail. Se o estorno já foi confirmado, você também receberá um comprovante de reembolso.',
    ];
  }

  if (input.outcome === 'prorated_refund') {
    const valor = input.formatarMoeda(input.refundCents);
    return [
      'Sua assinatura foi cancelada.',
      `Foi iniciado um reembolso proporcional de ${valor} pelo tempo não utilizado.`,
      `A devolução será feita pelo ${meio} da compra original. ${prazo}`,
      'Seu acesso à plataforma foi encerrado. O progresso permanece salvo.',
    ];
  }

  // no_refund — fora dos 7 dias
  return [
    'Sua assinatura foi cancelada e não será renovada automaticamente.',
    `Você continua com acesso completo até ${ate} — o fim do período já pago.`,
    'Depois dessa data o acesso é suspenso, mas flashcards, revisões e anotações ficam guardados.',
    'Se mudou de ideia antes do vencimento, basta assinar de novo em Planos.',
    'Enviamos um e-mail com estes detalhes para o seu endereço cadastrado.',
  ];
}
